import { inject, Injectable } from '@angular/core';
import {
    ZebraBrowserPrintService,
    ZebraPrintZplOptions,
} from './zebra-browser-print.service';
import { NotificationsService } from '@pf/shared-ui';
import { PrintLabelOptions } from '../models/print-options.model';
import { concatMap, from, map, Observable, of, tap, toArray } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationType, ToastMessage, ToastType } from '@pf/shared-common';
import { uniq } from 'lodash-es';

@Injectable({
    providedIn: 'root',
})
export class PrintJobService {
    private readonly zebraPrintService = inject(ZebraBrowserPrintService);
    private readonly notification = inject(NotificationsService);

    createPrintJob(
        labels: PrintLabelOptions[],
        copies: number,
        options: ZebraPrintZplOptions
    ): Observable<boolean> {
        const printJobs = labels.flatMap(label =>
            Array.from({ length: copies }, () => label)
        );

        if (printJobs.length === 0) {
            return of(true);
        }

        const startJobToastRef = this.notification.create({
            type: NotificationType.ToastMessage,
            toastType: ToastType.info,
            message: `Queued ${printJobs.length} labels to print.`,
            persist: true,
        } as ToastMessage);

        return from(printJobs).pipe(
            concatMap(label =>
                this.zebraPrintService.printZpl(label.zpl, options).pipe(
                    map(() => ({ success: true, label, error: null })),
                    catchError(err => of({ success: false, error: err, label }))
                )
            ),
            toArray(),
            tap(() => {
                this.notification.remove(startJobToastRef);
            }),
            map(results => {
                const errors = results.filter(r => !r.success);
                if (errors.length > 0) {
                    const errorMessages = uniq(
                        errors
                            .map(e =>
                                typeof e.error === 'string'
                                    ? e.error
                                    : (e.error as Error)?.message
                            )
                            .filter(Boolean)
                    );
                    this.notification.create({
                        type: NotificationType.ToastMessage,
                        toastType: ToastType.error,
                        message: `${errors.length} of ${printJobs.length} labels failed to print. ${errorMessages.join(', ')}`,
                        duration: 2000,
                    } as ToastMessage);
                    return false;
                } else {
                    this.notification.create({
                        type: NotificationType.ToastMessage,
                        toastType: ToastType.success,
                        message: `${printJobs.length} labels sent to printer.`,
                        duration: 2000,
                    } as ToastMessage);
                    return true;
                }
            })
        );
    }
}
